import Button from '@jetbrains/ring-ui/components/button/button'
import Dialog from '@jetbrains/ring-ui/components/dialog/dialog'
import memoize from '@jetbrains/ring-ui/components/global/memoize'
import {H1} from '@jetbrains/ring-ui/components/heading/heading'
import {Content} from '@jetbrains/ring-ui/components/island/island'
import Panel from '@jetbrains/ring-ui/components/panel/panel'
import {useCallback, useMemo} from 'react'
import {createSelector} from 'reselect'

import {toggleOverview} from '../../../actions/overview'
import withDialogContainer from '../../../hocs/withDialogContainer'
import type {DialogProps} from '../../../hocs/withDialogContainer'
import useOneOffSelector from '../../../hooks/useOneOffSelector'
import {useSlice} from '../../../hooks/useSlice'
import type {State} from '../../../reducers/types'
import {
  getBuildTypesHash,
  getOverviewBuildTypesHash,
  getOverviewData,
  getOverviewProjectsHash,
  getProjectsHash,
} from '../../../selectors'
import type {ProjectsTree as ProjectsTreeType, TreeFilter} from '../../../selectors/projectTrees'
import {
  createAllProjectsTitleItem,
  createDefaultOverviewTree,
  getAllProjectsExpandState,
  getAllProjectsLoaded,
  getHasFavoriteProjects,
  getIsStarred,
  getOverviewExpandState,
  getProjectsAndOverviewReady,
  getProjectsData,
  mergeProjectsForSidebar,
} from '../../../selectors/projectTrees'
import type {ExpandState, ProjectId, ProjectOrBuildTypeNode} from '../../../types'
import {DialogType, ROOT_PROJECT_ID} from '../../../types'
import {currentServerId} from '../../../types/projectTrees'
import type {ProjectsTreeItem} from '../../../types/projectTrees'
import {convertDataToTree, upgradeTreeByExpandState} from '../../../utils/convertDataToTree'
import {noop} from '../../../utils/empty'
import {cmdOrCtrl} from '../../../utils/keys'
import {keyValue} from '../../../utils/object'
import type {KeyValue} from '../../../utils/object'
import ProjectsTree from '../ProjectsTree/ProjectsTree'
import ProjectTreeSearch from '../ProjectsTree/ProjectTreeSearch/ProjectTreeSearch'

import {moveBuildType, moveProject, resetOrderInRootProject} from './EditProjectSidebar.actions'
import {EditProjectSidebarContext} from './EditProjectSidebar.context'
import {useSearchItems} from './EditProjectSidebar.hooks'

import styles from './EditProjectSidebar.css'

import {useAppDispatch, useAppSelector} from 'src/hooks/react-redux'

type EditProjectSidebarDialogState = {
  searchQuery: string
  overviewExpandState: KeyValue<ProjectId, ExpandState>
  allProjectsExpandState: KeyValue<ProjectId, ExpandState>
  searchProjectsExpandState: KeyValue<ProjectId, ExpandState>
}

const StateKeyByGroup = {
  favorites: 'overviewExpandState',
  all: 'allProjectsExpandState',
  search: 'searchProjectsExpandState',
} as const
const header = (
  <div>
    <H1 className={styles.header}>{'Configure Favorites'}</H1>
    <div className={styles.shortcuts}>
      {'Toggle favorites: Enter'}
      <br />
      {`without subprojects: ${cmdOrCtrl}+Enter`}
      <br />
      {`Move: ${cmdOrCtrl}+↑↓`}
    </div>
  </div>
)

const makeGetItems = () => {
  const getAllProjectsTree: (
    state: State,
    filter: TreeFilter,
  ) => ProjectsTreeType | null | undefined = createSelector(
    getAllProjectsLoaded,
    (state: State) => getProjectsData(state),
    getBuildTypesHash,
    getProjectsHash,
    (_: State, filter: TreeFilter) => filter,
    (ready, data, buildTypes, projects, filter) =>
      convertDataToTree({
        ready,
        data: data.filter(id => !projects[id]?.archived),
        buildTypes,
        projects,
        group: 'all',
        filter,
      }),
  )
  return createSelector(
    (state: State) => getProjectsAndOverviewReady(state),
    getOverviewData,
    getOverviewBuildTypesHash,
    getOverviewProjectsHash,
    getProjectsHash,
    getHasFavoriteProjects,
    getAllProjectsTree,
    (state: State) => state.togglingOverview,
    (_: State, filter: TreeFilter) => filter,
    (_: State, __: TreeFilter, overviewExpandState: KeyValue<ProjectId, ExpandState>) =>
      overviewExpandState,
    (
      _: State,
      __: TreeFilter,
      ___: KeyValue<ProjectId, ExpandState>,
      allProjectsExpandState: KeyValue<ProjectId, ExpandState> | null | undefined,
    ) => allProjectsExpandState,
    (
      overviewReady,
      overviewData,
      overviewBuildTypesHash,
      overviewProjectsHash,
      allProjectsHash,
      hasFavoriteProjects,
      allProjectsTree,
      togglingOverview,
      filter,
      overviewExpandState,
      allProjectsExpandState,
    ) => {
      const defaultOverviewTree = createDefaultOverviewTree(
        overviewReady,
        overviewData,
        overviewBuildTypesHash,
        overviewProjectsHash,
        allProjectsHash,
        filter,
      )
      const overviewTree = upgradeTreeByExpandState({
        defaultTree: defaultOverviewTree,
        expandState: overviewExpandState,
        baseLevel: 0,
      })
      const getHasReorderedSiblings = memoize((itemType: string) =>
        memoize((parentId: ProjectId) => {
          const overviewSiblings: ProjectsTreeItem[] = []
          const siblings: ProjectsTreeItem[] = []
          const overviewTreeData = overviewTree.data
          const overviewTreeLength = overviewTreeData.length
          /* Info: Performance improvement
           * Here is using getting the project through the key
           * to avoid looping all projects every time and decrease amount of nested loops
           */
          for (let i = 0; i < overviewTreeLength; i++) {
            const item = overviewTreeData[i]

            if (item.itemType === itemType && item.parentId === parentId) {
              const hashKey = `${currentServerId}_${item.itemType}_${item.id}`
              const hashItem = allProjectsTree?.hash?.get(hashKey)

              overviewSiblings.push(item)

              if (hashItem) {
                siblings.push(hashItem)
              }
            }
          }

          siblings.sort(({index}, {index: prevIndex}) => index! - prevIndex!)

          return overviewSiblings.some(function someOoverviewSiblings(sibling, i) {
            return siblings[i]?.id !== sibling.id
          })
        }),
      )
      const overviewDataSet = new Set(overviewData)
      const upgradedOverviewData = overviewTree.data.map((item: ProjectsTreeItem) => ({
        ...item,
        isStarred: getIsStarred({
          item,
          togglingOverview,
          overviewData: overviewDataSet,
          overviewProjectsHash,
        }),
        hasReorderedChildren:
          item.itemType === 'project' &&
          (getHasReorderedSiblings('project')(item.id) ||
            getHasReorderedSiblings('buildType')(item.id)),
      }))
      const upgradedAllProjectsData = upgradeTreeByExpandState({
        defaultTree: allProjectsTree,
        expandState: allProjectsExpandState,
        baseLevel: 0,
        shift: -1,
      }).data.map((item: ProjectsTreeItem) => ({
        ...item,
        isStarred: getIsStarred({
          item,
          togglingOverview,
          overviewData: overviewDataSet,
          overviewProjectsHash,
        }),
      }))
      return {
        items: mergeProjectsForSidebar(
          upgradedOverviewData,
          hasFavoriteProjects,
          upgradedAllProjectsData,
          createAllProjectsTitleItem(false, true),
          false,
          false,
          true,
        ),
        rootHasReorderedChildren: getHasReorderedSiblings('project')(ROOT_PROJECT_ID),
      }
    },
  )
}

type Props = {
  projectId: ProjectId
}

function EditProjectSidebarDialogContent({projectId}: Props) {
  const initialOverviewExpandState = useOneOffSelector(getOverviewExpandState)
  const initialAllProjectsExpandState = useOneOffSelector(getAllProjectsExpandState)
  const [
    {searchQuery, overviewExpandState, allProjectsExpandState, searchProjectsExpandState},
    {setSearchQuery, toggleExpandState},
  ] = useSlice<
    EditProjectSidebarDialogState,
    {
      setSearchQuery: string
      toggleExpandState: {
        id: ProjectId
        expanded: boolean
        group: keyof typeof StateKeyByGroup
      }
    }
  >(
    {
      setSearchQuery: (state, query) => ({
        ...state,
        searchQuery: query,
        searchProjectsExpandState: {},
      }),

      toggleExpandState(state, {id, expanded, group}) {
        const key = StateKeyByGroup[group]
        return Object.assign(
          {},
          state,
          keyValue(key, {
            ...state[key],
            ...keyValue(id, {
              id,
              expandState: expanded ? 'EXPANDED' : 'COLLAPSED',
            }),
          }),
        )
      },
    },
    {
      searchQuery: '',
      overviewExpandState: initialOverviewExpandState,
      allProjectsExpandState: initialAllProjectsExpandState,
      searchProjectsExpandState: {},
    },
  )
  const getItems = useMemo(makeGetItems, [])
  const filter = useMemo(() => ({parentId: projectId}), [projectId])
  const searchItems = useSearchItems(filter, searchQuery, searchProjectsExpandState)
  const {items, rootHasReorderedChildren} = useAppSelector(state =>
    searchQuery.length > 0
      ? searchItems
      : getItems(state, filter, overviewExpandState, allProjectsExpandState),
  )
  const ready = useAppSelector(getProjectsAndOverviewReady)
  const dispatch = useAppDispatch()
  const toggleOverviewAction = useCallback(
    (node: ProjectOrBuildTypeNode, on: boolean, skipSubprojects?: boolean) =>
      dispatch(toggleOverview(node, on, skipSubprojects)),
    [dispatch],
  )
  const onChangeSearchActive = useCallback(
    (active: boolean) => {
      if (!active) {
        setSearchQuery('')
      }
    },
    [setSearchQuery],
  )
  return (
    <>
      {header}
      <Content
        fade={false}
        className={styles.content}
        scrollableWrapperClassName={styles.scrollableWrapper}
      >
        <EditProjectSidebarContext.Provider value>
          <ProjectsTree
            dispatch={dispatch}
            isEdit
            ready={ready}
            className={styles.tree}
            contentClassName={styles.treeContent}
            itemParentClassName={styles.treeItemParent}
            items={items}
            toggleExpandState={(id, expanded, group) => {
              if (group !== 'none') {
                toggleExpandState({
                  id,
                  expanded,
                  group,
                })
              }
            }}
            toggleExpandAll={noop}
            toggleOverview={toggleOverviewAction}
            searchActive
            searchQuery={searchQuery}
            renderSearch={({sidebarRef, collapsed, ...searchProps}) => (
              <ProjectTreeSearch
                {...searchProps}
                className={styles.search}
                searchQuery={searchQuery}
                onChangeQuery={setSearchQuery}
                onChangeActive={onChangeSearchActive}
                propagateEscape
              />
            )}
            moveProject={(...args) => dispatch(moveProject(...args))}
            moveBuildType={(...args) => dispatch(moveBuildType(...args))}
          >
            <Panel className={styles.footer}>
              {'Changes are applied automatically'}
              {rootHasReorderedChildren && (
                <Button
                  className={styles.resetOrder}
                  onClick={() => dispatch(resetOrderInRootProject())}
                >
                  {'Reset to default order'}
                </Button>
              )}
            </Panel>
          </ProjectsTree>
        </EditProjectSidebarContext.Provider>
      </Content>
    </>
  )
}

export function EditProjectSidebarDialog({isOpened, closeDialog, projectId}: DialogProps<Props>) {
  return (
    <Dialog
      label="Configure Sidebar"
      show={isOpened}
      onCloseAttempt={closeDialog}
      contentClassName={styles.dialog}
      showCloseButton
    >
      <EditProjectSidebarDialogContent projectId={projectId} />
    </Dialog>
  )
}

export default withDialogContainer<Props>(
  DialogType.EDIT_SIDEBAR,
  (_, {projectId}) => projectId,
)(EditProjectSidebarDialog)
